@if (loading()) {
  <cue-loading />
} @else {
  <div class="inner">
    <div class="header">
      <h2 class="title">{{ title() }}</h2>

      @if (isAnonymous() && localeService.languageItems.length > 1) {
        <div class="language-wrapper">
          <assist-dropdownlist
            [popupSettings]="{ appendTo: 'component', popupClass: 'language-popup', width: 'auto' }"
            [data]="localeService.languageItems"
            [formControl]="currentLanguageControl"
            textField="text"
            valueField="text"
            class="language-dropdownlist"
          >
            <ng-template let-dataItem valueTemplate>
              <div class="flag-wrapper">
                <img [src]="'assets/images/flags/' + dataItem?.flag" alt="" class="flag" height="30" width="30" />
                <cue-icon [icon]="ICONS.cueArrowDown" />
              </div>
            </ng-template>
            <ng-template itemTemplate let-dataItem>
              <img [src]="'assets/images/flags/' + dataItem.flag" alt="" class="flag" height="30" width="30" />
              <span class="lang-shortcut">{{ dataItem.text }}</span>
            </ng-template>
          </assist-dropdownlist>
        </div>
      }

      <button type="button" aria-label="Close" class="close-btn" (click)="dialogRef.close()">
        <cue-icon [icon]="ICONS.cueClose" />
      </button>
    </div>
    <div class="delimiter"></div>

    <div class="content px-[5px]">
      @switch (activeBarItem()) {
        @case ('detail') {
          @if (isResourceSource()) {
            <assist-multiview-renew-detail-resource
              [resource]="resource()"
              [serverDetail]="serverDetailValue()"
              [anonymous]="isAnonymous()"
            />
          } @else if (isQrSource() && qrSourceDetailValue(); as detail) {
            <assist-multiview-renew-detail-qr [item]="detail" />
          }
        }
        @case ('navigation') {
          <assist-multiview-renew-navigation
            [dialogRef]="dialogRef"
            [isAnonymous]="isAnonymous()"
            [areaId]="areaId()"
            [resourceId]="resourceId() ?? undefined"
            [qrCodeId]="isQrSource() ? multiviewQr() : undefined"
          />
        }
        @case ('events') {
          @if (resource(); as selectedResource) {
            <assist-multiview-renew-events
              [resource]="selectedResource"
              [isAnonymous]="isAnonymous()"
              [notificationContainer]="notificationContainer"
              [qrScanTimestamp]="qrScanTimestamp()"
            />
          }
        }
        @case ('requests') {
          @if (resource(); as selectedResource) {
            <assist-multiview-renew-requests [resource]="selectedResource" />
          }
        }
        @case ('control') {
          <assist-multiview-renew-control [controls]="controls()" />
        }
        @case ('device') {
          <assist-multiview-renew-device [unitInfos]="unitInfos()" [loading]="false" />
        }
        @case ('more') {
          <assist-multiview-renew-more
            [qrCodeId]="isQrSource() ? multiviewQr() : undefined"
            [resourceId]="isResourceSource() ? multiviewResource() : undefined"
            qrQueryParam="multiviewQr"
          />
        }
        @default {
          <div class="empty-state">
            <span class="text-neutral-500">
              @if (isResourceSource()) {
                {{ translation.multiview.resourceNotFound | transloco }}
              } @else {
                {{ translation.multiview.poiNotFound | transloco }}
              }
            </span>
          </div>
        }
      }
    </div>
  </div>

  <div class="bottom-bar">
    @if (hasBarItem('detail')) {
      <button
        type="button"
        class="bottom-bar-item"
        [class.active]="activeBarItem() === 'detail'"
        [attr.aria-pressed]="activeBarItem() === 'detail'"
        (click)="setActiveBarItem('detail')"
      >
        <cue-icon [icon]="ICONS.cueInfo" [theme]="activeBarItem() === 'detail' ? 'primary-500' : 'neutral'" />
        <span>{{ translation.multiview.tabs.detail | transloco }}</span>
      </button>
    }

    @if (hasBarItem('navigation')) {
      <button
        type="button"
        class="bottom-bar-item"
        [class.active]="activeBarItem() === 'navigation'"
        [attr.aria-pressed]="activeBarItem() === 'navigation'"
        (click)="setActiveBarItem('navigation')"
      >
        <cue-icon
          [icon]="ICONS.cueNavigationCircle"
          [theme]="activeBarItem() === 'navigation' ? 'primary-500' : 'neutral'"
        />
        <span>{{ translation.multiview.tabs.navigation | transloco }}</span>
      </button>
    }

    @if (hasBarItem('events')) {
      <button
        type="button"
        class="bottom-bar-item"
        [class.active]="activeBarItem() === 'events'"
        [attr.aria-pressed]="activeBarItem() === 'events'"
        (click)="setActiveBarItem('events')"
      >
        <cue-icon [icon]="ICONS.cueCalendarCheck" [theme]="activeBarItem() === 'events' ? 'primary-500' : 'neutral'" />
        <span>{{ translation.multiview.tabs.events | transloco }}</span>
      </button>
    }

    @if (hasBarItem('requests')) {
      <button
        type="button"
        class="bottom-bar-item"
        [class.active]="activeBarItem() === 'requests'"
        [attr.aria-pressed]="activeBarItem() === 'requests'"
        (click)="setActiveBarItem('requests')"
      >
        <cue-icon [icon]="ICONS.cueOrders" [theme]="activeBarItem() === 'requests' ? 'primary-500' : 'neutral'" />
        <span>{{ translation.multiview.tabs.requests | transloco }}</span>
      </button>
    }

    @if (hasBarItem('control')) {
      <button
        type="button"
        class="bottom-bar-item"
        [class.active]="activeBarItem() === 'control'"
        [attr.aria-pressed]="activeBarItem() === 'control'"
        (click)="setActiveBarItem('control')"
      >
        <cue-icon [icon]="ICONS.cueTablet" [theme]="activeBarItem() === 'control' ? 'primary-500' : 'neutral'" />
        <span>{{ translation.multiview.tabs.control | transloco }}</span>
      </button>
    }

    @if (hasBarItem('device')) {
      <button
        type="button"
        class="bottom-bar-item"
        [class.active]="activeBarItem() === 'device'"
        [attr.aria-pressed]="activeBarItem() === 'device'"
        (click)="setActiveBarItem('device')"
      >
        <cue-icon [icon]="ICONS.cueSpeaker" [theme]="activeBarItem() === 'device' ? 'primary-500' : 'neutral'" />
        <span>{{ translation.multiview.tabs.device | transloco }}</span>
      </button>
    }

    @if (hasBarItem('more')) {
      <button
        type="button"
        class="bottom-bar-item"
        [class.active]="activeBarItem() === 'more'"
        [attr.aria-pressed]="activeBarItem() === 'more'"
        (click)="setActiveBarItem('more')"
      >
        <cue-icon [icon]="ICONS.cueUser" [theme]="activeBarItem() === 'more' ? 'primary-500' : 'neutral'" />
        <span>{{ translation.multiview.tabs.login | transloco }}</span>
      </button>
    }
  </div>
}
